import { useCallback, useMemo, useRef, type Dispatch, type SetStateAction } from 'react';

import { type EditorNextProps } from '@atlaskit/editor-core/composable-editor';
import FeatureGates from '@atlaskit/feature-gate-js-client';

import { getActualTextContent } from '../../../../common/utils/node/getActualTextContent';
import type { PromptContentChangePayload } from '../../prompt-content-change-payload';

type EditorChangeEvent = Parameters<NonNullable<EditorNextProps['onChange']>>[0];

type UseInlineEditorPropsArgs = {
	assistiveLabel: string;
	defaultValue: EditorNextProps['defaultValue'];
	mentionResource: EditorNextProps['mentionProvider'];
	onPromptContentChange?: (payload: PromptContentChangePayload) => void;
	popupsMountPoint?: HTMLElement | null;
	preset: EditorNextProps['preset'];
	setHasMentionOrInlineCard: Dispatch<SetStateAction<boolean>>;
	setInputValue: Dispatch<SetStateAction<string>>;
	stableOnSave: NonNullable<EditorNextProps['onSave']>;
};

const hasMentionOrInlineCard = (doc: EditorChangeEvent['state']['doc']) => {
	let hasMentionOrCard = false;
	doc.descendants((node) => {
		if (
			node.type.name === 'mention' ||
			node.type.name === 'inlineCard' ||
			node.type.name === 'inlineExtension'
		) {
			hasMentionOrCard = true;
			return false;
		}
		return true;
	});
	return hasMentionOrCard;
};

export const useInlineEditorProps = ({
	assistiveLabel,
	defaultValue,
	mentionResource,
	onPromptContentChange,
	popupsMountPoint,
	preset,
	setHasMentionOrInlineCard,
	setInputValue,
	stableOnSave,
}: UseInlineEditorPropsArgs): EditorNextProps => {
	const sharedEditorProps = useMemo<Omit<EditorNextProps, 'onSave' | 'onChange'>>(() => {
		return {
			assistiveLabel,
			minHeight: 20,
			shouldFocus: true,
			preset,
			mentionProvider: mentionResource,
			maxHeight: 200,
			appearance: 'chromeless',
			popupsMountPoint: popupsMountPoint ?? undefined,
			defaultValue,
		};
	}, [assistiveLabel, defaultValue, mentionResource, popupsMountPoint, preset]);

	const stableEditorPropsLatest = {
		onPromptContentChange,
		setHasMentionOrInlineCard,
		setInputValue,
	};
	const stableEditorPropsLatestRef = useRef(stableEditorPropsLatest);
	stableEditorPropsLatestRef.current = stableEditorPropsLatest;

	const stableOnChange: NonNullable<EditorNextProps['onChange']> = useCallback((ev, meta) => {
		const hasMentionOrCard = hasMentionOrInlineCard(ev.state.doc);
		const latest = stableEditorPropsLatestRef.current;
		if (!meta.isDirtyChange) {
			latest.setHasMentionOrInlineCard(hasMentionOrCard);
		}
		latest.setInputValue(getActualTextContent(ev.state.doc));

		latest.onPromptContentChange?.({
			textLength: ev.state.doc.textContent.length,
			isEmpty: ev.state.doc.textContent.trim().length === 0,
			hasMentionOrInlineCard: hasMentionOrCard,
		});
		// Intentional empty deps: mutable values are always read from stableEditorPropsLatestRef at call time.
	}, []);

	const stableEditorProps: EditorNextProps = useMemo(() => {
		return {
			...sharedEditorProps,
			onSave: stableOnSave,
			onChange: stableOnChange,
		};
	}, [sharedEditorProps, stableOnChange, stableOnSave]);

	// Keep the control path un-memoized so experiment-off preserves the existing editor prop/callback churn baseline.
	const controlEditorProps: EditorNextProps = {
		...sharedEditorProps,
		onSave: (view) => stableOnSave(view),
		onChange: (ev, meta) => {
			const hasMentionOrCard = hasMentionOrInlineCard(ev.state.doc);

			if (!meta.isDirtyChange) {
				setHasMentionOrInlineCard(hasMentionOrCard);
			}
			setInputValue(getActualTextContent(ev.state.doc));

			onPromptContentChange?.({
				textLength: ev.state.doc.textContent.length,
				isEmpty: ev.state.doc.textContent.trim().length === 0,
				hasMentionOrInlineCard: hasMentionOrCard,
			});
		},
	};

	return FeatureGates.getExperimentValue<boolean>(
		'rovo_ttai_segment_render_stabilization',
		'isEnabled',
		false,
	) === true
		? stableEditorProps
		: controlEditorProps;
};
